import { Remark } from '@oneaudi/onegraph-client';
import { Translations } from '~/app/i18n/i18n';

import { MarketIdentifier } from '~/app/market-config';
import { MarketFormatter } from '~/app/market-config/market-formatter';
import getTranslatedLabel from '~/app/utils/get-tranlslated-label';
import {
	TechnicalDataForConfiguration,
	TechnicalDataForModel,
	TechnicalDataItemWithKey,
	VehicleType,
} from '~/graphql/__generated__/graphql';

export const AVAILABLE_KEY_FACTS = [
	'acceleration',
	'accelerationLaunchControl',
	'maxPowerHp',
	'maxPower',
	'maxPowerLaunchControl',
	'maxOutputExtraMaxOutput',
	'maxKilowatts',
	'speed',
	'torque',
	'batteryNetCapacity',
	'electricRangeCombined',
	'electricRanges',
	'electricRanges10',
	'chargingTime',
	'classCO2',
	'classCO2Sustaining',
] as const;

export type AvailableKeyFact = (typeof AVAILABLE_KEY_FACTS)[number];

export type ValueUnitLabel = {
	value?: string | number | null | undefined;
	unit?: string | null | undefined;
	label?: string | null | undefined;
	formattedValue?: string | null | undefined;
	remarks?: (Remark | null)[] | null | undefined;
};

export type KeyFactsConfig = KeyFactGetter[];

type KeyFactGetter<K extends AvailableKeyFact = AvailableKeyFact> = {
	key: K;
	getFn: (
		techData: TechnicalDataForModel | TechnicalDataForConfiguration,
		translations: Translations,
		formatter: MarketFormatter,
		market: MarketIdentifier,
		vehicleType?: VehicleType | null,
	) => ValueUnitLabel | null | undefined | false;
};

function mapToValueUnitLabel(
	item: TechnicalDataItemWithKey | null | undefined,
	translations: Translations,
): ValueUnitLabel | null {
	if (!item) return null;
	const label = getTranslatedLabel(translations, item.item.translationKey, item.item.label);
	if (!label) return null;
	return {
		value: item.item.formattedValue,
		label,
		formattedValue: item.item.formattedValue,
		remarks: item.item.remarks,
	};
}

export const KEY_FACT: {
	[K in AvailableKeyFact]: KeyFactGetter<K>;
} = {
	acceleration: {
		key: 'acceleration',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'acceleration'),
				translations,
			),
	},
	accelerationLaunchControl: {
		key: 'accelerationLaunchControl',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'acceleration-mlc'),
				translations,
			),
	},
	speed: {
		key: 'speed',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'top-speed'),
				translations,
			),
	},
	maxPower: {
		key: 'maxPower',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'max-output'),
				translations,
			),
	},
	maxPowerHp: {
		key: 'maxPowerHp',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'max-output-ps'),
				translations,
			),
	},
	maxPowerLaunchControl: {
		key: 'maxPowerLaunchControl',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'max-electrical-power-mlc'),
				translations,
			),
	},
	maxOutputExtraMaxOutput: {
		key: 'maxOutputExtraMaxOutput',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'max-output-extra-max-output'),
				translations,
			),
	},
	maxKilowatts: {
		key: 'maxKilowatts',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'max-output-kw'),
				translations,
			),
	},
	electricRangeCombined: {
		key: 'electricRangeCombined',
		getFn: (techData, translations) =>
			techData?.modelAttributes?.driveTrain === 'BEV' &&
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'max-range-charging-dc-10'),
				translations,
			),
	},
	electricRanges: {
		key: 'electricRanges',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'electric-range'),
				translations,
			),
	},
	electricRanges10: {
		key: 'electricRanges10',
		getFn: (techData, translations) => {
			return (
				techData?.modelAttributes?.driveTrain === 'BEV' &&
				mapToValueUnitLabel(
					techData?.items.find((item) => item.key === 'max-range-charging-dc-10'),
					translations,
				)
			);
		},
	},
	chargingTime: {
		key: 'chargingTime',
		getFn: (techData, translations) => {
			if (techData?.modelAttributes?.driveTrain === 'PHEV' || techData?.modelAttributes?.driveTrain === 'BEV')
				return mapToValueUnitLabel(
					techData?.items.find((item) => item.key === 'battery-charging-dc-10-80'),
					translations,
				);
		},
	},
	torque: {
		key: 'torque',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'max-torque'),
				translations,
			),
	},
	batteryNetCapacity: {
		key: 'batteryNetCapacity',
		getFn: (techData, translations) => {
			if (techData?.modelAttributes?.driveTrain !== 'BEV' && techData?.modelAttributes?.driveTrain !== 'PHEV')
				return null;
			return mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'battery-energy-content-net'),
				translations,
			);
		},
	},
	classCO2: {
		key: 'classCO2',
		getFn: (techData, translations) =>
			mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'eec'),
				translations,
			),
	},
	classCO2Sustaining: {
		key: 'classCO2Sustaining',
		getFn: (techData, translations, __, _, vehicleType) => {
			if (vehicleType !== 'PHEV') return null;
			return mapToValueUnitLabel(
				techData?.items.find((item) => item.key === 'eecDischarged'),
				translations,
			);
		},
	},
};

export const DEFAULT_KEY_FACTS: KeyFactsConfig = [
	KEY_FACT.electricRangeCombined,
	KEY_FACT.chargingTime,
	KEY_FACT.maxPower,
];
